import React from 'react';
import { Alert, Badge, Button, ButtonGroup, Card, CardBody, Input, Table, UncontrolledTooltip } from 'reactstrap';
import { auth } from '../../Api';
import { formatServerMessage } from '../../utils/serverErrors';
import { monthLabel } from '../../utils/invoices';
import {
	FILTERS, ALL, filterCompanies, filterCounts, sortCompanies, latestState, pageOf, companiesCsv, downloadText, periodOptions, shortDate,
	freeAgentMark, freeAgentLines, freeAgentReturn, remindersText, remindersShort
} from '../../utils/billingOverview';
import RowMenu from '../account/row-menu';
import EditBillingDialog from './edit-billing-dialog';
import { trialBadge } from '../../utils/signup';
import ResellerCommission from './reseller-commission';
import { rememberAccount } from '../../utils/accountType';
import '../account/billing-tab.scss';
import './billing-overview.scss';

const PAGE_SIZE = 50;

// The super admin's Billing page (laravel BillingController@companies, App\BillingOverview): Mobiliis income for a
// month, the year so far or all time, and every company on one slim row (also those not billed yet): billing start
// and price, this month so far and predicted, the latest invoice, what is outstanding, and the chosen period's
// totals. Sortable, filtered and searched here (the server sends every company at once, with a fixed number of
// queries), 50 rows a page. The price and billing start date are edited here (one company or several ticked).
// A company's name opens its Account > Billing tab. The FreeAgent box connects Martin's FreeAgent (bookkeeping only,
// laravel FreeAgentController) and each latest invoice shows whether it is in FreeAgent. Reseller commission
// (App\Commission): the commission and the income after it with the figures, and the Reseller commission section
// (reseller-commission.js) under the table. Reminders: those sent for the latest invoice; "Suspended" with a crossed-out $ is a company suspended for non-payment.
// Back on this browser tab, the page asks again quietly for the chosen period (not while a dialog is open or FreeAgent busy).
class BillingOverview extends React.Component {
	constructor(props) {
		super(props);
		// Back from connecting FreeAgent (?freeagent=connected | failed)
		this.state = {
			loading: true,
			error: '',
			notice: '',
			// { tone, text } until closed (loading the page does not clear it)
			freeAgentBack: freeAgentReturn(props.location && props.location.search),
			// connect | sync | disconnect while it runs
			freeAgentBusy: null,
			data: null,
			period: null,
			filter: ALL,
			search: '',
			sort: 'name',
			descending: false,
			page: 0,
			selected: {},
			// [companies] while the Edit billing dialog is open
			editing: null,
			// The Reseller commission section's Mark paid dialog is open
			commissionDialog: false
		};
		this.onVisible = this.onVisible.bind(this);
		// When the data was last asked for (ms)
		this.loadedAt = 0;
	}

	componentDidMount() {
		if (freeAgentReturn(this.props.location && this.props.location.search) && this.props.history) {
			this.props.history.replace('/billing-overview');
		}
		this.load(null);
		document.addEventListener('visibilitychange', this.onVisible);
	}

	componentWillUnmount() {
		document.removeEventListener('visibilitychange', this.onVisible);
	}

	// Back on this browser tab: the same period again, quietly (the filter, search, sort, page and ticks stay); not while
	// the Edit billing dialog is open or FreeAgent is busy, and not more than once in 5 seconds
	onVisible() {
		const { data, loading, editing, freeAgentBusy, period, commissionDialog } = this.state;
		if (document.visibilityState !== 'visible' || !data || loading || editing || freeAgentBusy || commissionDialog || Date.now() - this.loadedAt < 5000) {
			return;
		}
		this.load(period, true);
	}

	superAdmin() {
		try {
			return localStorage.getItem('admin_level') === 'a';
		} catch (e) {
			return false;
		}
	}

	// quiet: in the background (back on this browser tab): what is shown stays until the new data is there, and a
	// failure changes nothing
	load(period, quiet) {
		if (!this.superAdmin()) {
			this.setState({ loading: false, error: 'This page is for the Mobiliis super admin only.' });
			return;
		}
		this.loadedAt = Date.now();
		if (quiet !== true) {
			this.setState({ loading: true, error: '' });
		}
		auth.getBillingCompanies(period ? { period } : {})
			.then((res) => {
				if (res.data.code == 200) {
					// A period chosen while a quiet reload was on its way wins
					if (quiet === true && (this.state.loading || this.state.period !== period)) {
						return;
					}
					this.setState({ loading: false, data: res.data, period: res.data.period });
				} else if (quiet !== true) {
					this.setState({ loading: false, error: formatServerMessage(res.data.message, 'Billing could not be loaded.') });
				}
			})
			.catch(() => {
				if (quiet !== true) {
					this.setState({ loading: false, error: 'Billing could not be loaded. Please try again.' });
				}
			});
	}

	// The company's Account > Billing tab, as the super admin (the admin website's chosen company)
	openCompany(row) {
		localStorage.setItem('companyId', row.id);
		localStorage.setItem('companyName', row.name);
		rememberAccount(row);
		this.props.history.push('/account?tab=billing');
	}

	sortBy(key) {
		this.setState((state) => ({ sort: key, descending: state.sort === key ? !state.descending : false, page: 0 }));
	}

	toggleSelected(id) {
		this.setState((state) => {
			const selected = { ...state.selected };
			if (selected[id]) {
				delete selected[id];
			} else {
				selected[id] = true;
			}
			return { selected };
		});
	}

	// Ticks (or unticks) every company in the filter and search, on all pages
	toggleAll(rows) {
		const all = rows.length > 0 && rows.every((row) => this.state.selected[row.id]);
		const selected = { ...this.state.selected };
		rows.forEach((row) => {
			if (all) {
				delete selected[row.id];
			} else {
				selected[row.id] = true;
			}
		});
		this.setState({ selected });
	}

	saved(message) {
		this.setState({ editing: null, selected: {}, notice: message });
		this.load(this.state.period);
	}

	// To FreeAgent's page that lets Mobiliis in; FreeAgent then sends the browser back here
	connectFreeAgent() {
		this.setState({ freeAgentBusy: 'connect', error: '', notice: '' });
		auth.freeAgentConnect({ return_to: window.location.origin + '/billing-overview' })
			.then((res) => {
				if (res.data.code == 200 && res.data.url) {
					window.location.assign(res.data.url);
				} else {
					this.setState({ freeAgentBusy: null, error: formatServerMessage(res.data.message, 'FreeAgent could not be connected.') });
				}
			})
			.catch(() => this.setState({ freeAgentBusy: null, error: 'FreeAgent could not be connected. Please try again.' }));
	}

	syncFreeAgent() {
		this.setState({ freeAgentBusy: 'sync', error: '', notice: '' });
		auth.freeAgentSync({})
			.then((res) => {
				const done = res.data.code == 200 && res.data.result === 'done';
				const message = formatServerMessage(res.data.message, done ? 'Sent.' : 'The sync did not work.');
				this.setState((state) => ({
					freeAgentBusy: null,
					notice: done ? message : '',
					error: done ? '' : message,
					data: state.data && res.data.freeagent ? { ...state.data, freeagent: res.data.freeagent } : state.data
				}));
				// The rows' FreeAgent marks
				this.load(this.state.period);
			})
			.catch(() => this.setState({ freeAgentBusy: null, error: 'The sync did not work. Please try again.' }));
	}

	disconnectFreeAgent() {
		if (!window.confirm('Disconnect FreeAgent? Nothing more is sent until it is connected again. Nothing in FreeAgent is changed.')) {
			return;
		}
		this.setState({ freeAgentBusy: 'disconnect', error: '', notice: '' });
		auth.freeAgentDisconnect({})
			.then((res) => {
				const ok = res.data.code == 200;
				this.setState((state) => ({
					freeAgentBusy: null,
					notice: ok ? formatServerMessage(res.data.message, 'FreeAgent is disconnected.') : '',
					error: ok ? '' : formatServerMessage(res.data.message, 'FreeAgent could not be disconnected.'),
					data: state.data && res.data.freeagent ? { ...state.data, freeagent: res.data.freeagent } : state.data
				}));
			})
			.catch(() => this.setState({ freeAgentBusy: null, error: 'FreeAgent could not be disconnected. Please try again.' }));
	}

	// Martin's FreeAgent: connected to which company, the last sync, what is waiting or failed
	renderFreeAgent(status) {
		const box = freeAgentLines(status);
		const busy = this.state.freeAgentBusy;
		return (
			<div className={'billing-freeagent mb-2 border-' + (box.tone === 'danger' ? 'danger' : box.tone === 'success' ? 'success' : 'light')}>
				<div className="d-flex flex-wrap align-items-center justify-content-between">
					<div className="small">
						<strong className="mr-1">{box.title}:</strong>
						{box.lines.map((line, i) => <span key={i} className={'mr-1' + (i > 1 && box.tone === 'danger' ? ' text-danger' : '')}>{line}</span>)}
					</div>
					{status && status.available && status.configured && (
						<div className="text-nowrap">
							{!status.connected && (
								<Button size="sm" color="primary" disabled={!!busy} onClick={() => this.connectFreeAgent()}>
									{busy === 'connect' ? 'Opening FreeAgent...' : 'Connect FreeAgent'}
								</Button>
							)}
							{status.connected && (
								<React.Fragment>
									<Button size="sm" color="primary" className="mr-1" disabled={!!busy || !status.sync_on || status.busy}
										title={!status.sync_on ? 'Sending is off on this server (FREEAGENT_SYNC=false)' : 'Send what is waiting now, failed ones too'}
										onClick={() => this.syncFreeAgent()}>
										{busy === 'sync' ? 'Sending...' : 'Sync now'}
									</Button>
									<Button size="sm" color="secondary" outline disabled={!!busy} onClick={() => this.disconnectFreeAgent()}>Disconnect</Button>
								</React.Fragment>
							)}
						</div>
					)}
				</div>
			</div>
		);
	}

	downloadCsv(rows) {
		const { data } = this.state;
		const period = data.period === 'all' ? 'all-time' : data.period === 'year' ? data.current_month.slice(0, 4) + '-so-far' : data.period;
		downloadText('mobiliis-billing-' + period + '.csv', companiesCsv(rows, data.period_label, data.income.this_month.month_label));
	}

	renderIncome(data) {
		const income = data.income;
		const month = income.this_month;
		const commission = income.commission ? income.commission.period : null;
		const figure = (label, value, tone, help) => (
			<div className="billing-figure">
				<div className="text-muted small">{label}</div>
				<div className={'billing-figure-value' + (tone ? ' text-' + tone : '')}>USD {value}</div>
				{help && <div className="text-muted small">{help}</div>}
			</div>
		);
		return (
			<div className="d-flex flex-wrap mb-2">
				{figure('Invoiced, ' + data.period_label, income.invoiced_usd, null, income.invoices + ' ' + (income.invoices === 1 ? 'invoice' : 'invoices'))}
				{figure('Received', income.received_usd, 'success')}
				{figure('Outstanding', income.outstanding_usd, Number(income.outstanding_usd) > 0 ? 'warning' : null)}
				{figure('Overdue', income.overdue_usd, Number(income.overdue_usd) > 0 ? 'danger' : null,
					income.overdue_invoices > 0 ? income.overdue_invoices + ' ' + (income.overdue_invoices === 1 ? 'invoice' : 'invoices') : null)}
				{commission && figure('After reseller commission', commission.after_commission_usd, null,
					'commission ' + commission.earned_usd + ': ' + commission.owed_usd + ' owed, ' + commission.paid_usd + ' paid')}
				<div className="billing-figure billing-figure-month">
					<div className="text-muted small">
						{month.month_label}, all companies
						<i className="fa fa-info-circle ml-1" id="billing-overview-estimate-info" style={{ cursor: 'help' }} aria-label="How this is worked out" />
						<UncontrolledTooltip target="billing-overview-estimate-info" placement="bottom" className="mobiliis-tooltip">
							So far: the duty starts recorded this month, not invoiced yet. Predicted: each company's estimate on its Billing tab
							(the duties set up for the rest of the month), added up. An estimate, not an invoice. The invoices for this month are
							made on the 1st.
						</UncontrolledTooltip>
					</div>
					<div className="billing-figure-value">USD {month.usd} <span className="text-muted small">so far</span></div>
					<div className="small">about USD {month.estimate_usd} predicted by the end of the month</div>
				</div>
			</div>
		);
	}

	header(key, label, className) {
		const { sort, descending } = this.state;
		return (
			<th className={'billing-sortable ' + (className || '')} onClick={() => this.sortBy(key)} aria-sort={sort === key ? (descending ? 'descending' : 'ascending') : 'none'}>
				{label}{sort === key ? (descending ? ' ▼' : ' ▲') : ''}
			</th>
		);
	}

	// In FreeAgent (a link to it there), waiting, failed (the reason on hover), cancelled
	renderFreeAgentMark(latest) {
		const mark = freeAgentMark(latest.freeagent);
		if (!mark) {
			return null;
		}
		const icon = <i className={'mdi ' + mark.icon + ' billing-freeagent-mark text-' + mark.tone} />;
		return mark.link
			? <a href={mark.link} target="_blank" rel="noopener noreferrer" title={mark.label} aria-label={mark.label}>{icon}</a>
			: <span title={mark.label} aria-label={mark.label} className="billing-help">{icon}</span>;
	}

	renderRow(row) {
		const selected = !!this.state.selected[row.id];
		const latest = row.latest;
		const state = latestState(latest);
		const items = [
			{ label: 'Edit billing...', onClick: () => this.setState({ editing: [row] }) },
			{ label: 'Open Billing tab', onClick: () => this.openCompany(row) }
		];
		const priceId = 'billing-price-' + row.id;
		// A company that signed up: its free trial (App/Trial.php)
		const trial = trialBadge(row.trial, shortDate);
		return (
			<tr key={row.id} className={selected ? 'table-active' : ''}>
				<td className="align-middle">
					<Input type="checkbox" className="position-static m-0" checked={selected} aria-label={'Select ' + row.name}
						onChange={() => this.toggleSelected(row.id)} />
				</td>
				<td className={'align-middle billing-company' + (row.stopped ? ' billing-company-suspended' : '')}>
					<Button color="link" size="sm" className="p-0 text-left" title={row.name} onClick={() => this.openCompany(row)}>{row.name}</Button>
					{row.stopped && (
							<Badge color="danger" className="ml-1"
								title={row.stopped_for_nonpayment ? 'Suspended for non-payment of ' + (row.stopped_for_nonpayment.number || 'an invoice') + ' on ' + shortDate(row.stopped_for_nonpayment.at) + '; reinstated once paid' : 'Suspended by hand'}>
								Suspended{row.stopped_for_nonpayment && <i className="mdi mdi-currency-usd-off ml-1" aria-label="for non-payment" />}
							</Badge>
						)}
					{trial && <Badge color={trial.tone} className="ml-1" title={trial.title}>{trial.label}</Badge>}
				</td>
				<td className="align-middle text-muted billing-reseller" title={row.reseller || ''}>{row.reseller || ''}</td>
				<td className="align-middle" title={row.starts_on_label || ''}>{row.starts_on ? shortDate(row.starts_on) : <span className="text-muted">Not billed yet</span>}</td>
				<td className="align-middle text-right">
					{/* The default price in grey; * some clients have their own */}
					<span id={priceId} className={row.company_rate_usd === null ? 'text-muted' : ''}>{row.rate_usd}{row.clients_own_price ? ' *' : ''}</span>
					{row.clients_own_price && (
						<UncontrolledTooltip target={priceId} placement="right" className="mobiliis-tooltip">Some clients have their own price.</UncontrolledTooltip>
					)}
				</td>
				<td className="align-middle text-right">{row.this_month ? row.this_month.duty_starts : ''}</td>
				<td className="align-middle text-right">{row.this_month ? row.this_month.usd : ''}</td>
				<td className="align-middle text-right">{row.this_month ? row.this_month.estimate_usd : ''}</td>
				<td className="align-middle">
					{latest ? (
						<React.Fragment>
							{/* The month on hover (the row stays on one line) */}
							<span title={'For ' + latest.period_label} className="billing-help">{latest.number}</span> {latest.total_usd}{' '}
							{/* "Due 8 Nov 2026", "Overdue 4 days": short, so the row stays on one line (the full wording is in the CSV) */}
							<Badge color={state.tone}>{state.label.indexOf('Due by ') === 0 ? 'Due ' + shortDate(latest.due_date) : state.label.replace('Overdue by ', 'Overdue ')}</Badge>
							{this.renderFreeAgentMark(latest)}
						</React.Fragment>
					) : <span className="text-muted">None yet</span>}
				</td>
				<td className="align-middle text-nowrap" title={remindersText(latest)}>{remindersShort(latest)}</td>
				<td className={'align-middle text-right' + (row.overdue > 0 ? ' text-danger font-weight-bold' : '')}>{row.outstanding_usd}</td>
				<td className="align-middle text-right billing-period-start">{row.period.invoiced_usd}</td>
				<td className="align-middle text-right">{row.period.paid_usd}</td>
				<td className="align-middle text-right">{row.period.outstanding_usd}</td>
				<td className="align-middle text-right"><RowMenu items={items} title="Actions" label={<i className="mdi mdi-dots-horizontal" />} /></td>
			</tr>
		);
	}

	renderTable(data) {
		const { filter, search, sort, descending, page, selected } = this.state;
		const counts = filterCounts(data.companies);
		const rows = sortCompanies(filterCompanies(data.companies, filter, search), sort, descending);
		const shown = pageOf(rows, page, PAGE_SIZE);
		const ticked = data.companies.filter((row) => selected[row.id]);
		const allTicked = rows.length > 0 && rows.every((row) => selected[row.id]);
		const month = data.income.this_month.month_label;
		return (
			<React.Fragment>
				<div className="d-flex flex-wrap align-items-center mb-2">
					<ButtonGroup size="sm" className="mr-2 mb-1">
						{FILTERS.map((option) => (
							<Button key={option.id} color="secondary" outline={filter !== option.id} onClick={() => this.setState({ filter: option.id, page: 0 })}>
								{option.label} <span className="billing-count">{counts[option.id]}</span>
							</Button>
						))}
					</ButtonGroup>
					<Input bsSize="sm" type="search" className="mr-2 mb-1" style={{ width: 180 }} placeholder="Search by company name" value={search}
						aria-label="Search by company name" onChange={(e) => this.setState({ search: e.target.value, page: 0 })} />
					<Button size="sm" color="primary" className="mr-2 mb-1" disabled={ticked.length === 0}
						onClick={() => this.setState({ editing: ticked })}>
						Edit billing{ticked.length > 0 ? ' (' + ticked.length + ')' : ''}...
					</Button>
					<Button size="sm" color="secondary" outline className="mb-1" disabled={rows.length === 0} onClick={() => this.downloadCsv(rows)}>
						Download CSV
					</Button>
				</div>
				<Table size="sm" hover responsive className="billing-companies mb-1">
					<thead>
						<tr className="billing-group-row">
							<th colSpan="5"></th>
							<th colSpan="3" className="text-center">{month}</th>
							<th colSpan="3"></th>
							<th colSpan="3" className="text-center billing-period-start">{data.period_label}</th>
							<th></th>
						</tr>
						<tr>
							<th>
								<Input type="checkbox" className="position-static m-0" checked={allTicked} aria-label="Select all shown"
									title="Select every company in this list (all pages)" onChange={() => this.toggleAll(rows)} />
							</th>
							{this.header('name', 'Company')}
							{this.header('reseller', 'Reseller')}
							{this.header('starts_on', 'Billed from')}
							{this.header('rate', 'Price', 'text-right')}
							{this.header('starts', 'Starts', 'text-right')}
							{this.header('so_far', 'So far', 'text-right')}
							{this.header('predicted', 'Predicted', 'text-right')}
							{this.header('latest', 'Latest invoice')}
								{this.header('reminders', 'Reminders')}
							{this.header('outstanding', 'Outstanding', 'text-right')}
							{this.header('invoiced', 'Invoiced', 'text-right billing-period-start')}
							{this.header('paid', 'Paid', 'text-right')}
							{this.header('period_outstanding', 'Unpaid', 'text-right')}
							<th></th>
						</tr>
					</thead>
					<tbody>
						{shown.rows.map((row) => this.renderRow(row))}
					</tbody>
				</Table>
				{rows.length === 0 && <p className="text-muted small mb-1">No companies match.</p>}
				<div className="d-flex align-items-center justify-content-between small text-muted">
					<span>
						{rows.length > 0 && (shown.page * PAGE_SIZE + 1) + '-' + (shown.page * PAGE_SIZE + shown.rows.length) + ' of ' + rows.length}
						{rows.length !== data.companies.length && ' (' + data.companies.length + ' companies in all)'}
						{rows.length > 0 && ' · '}Amounts in USD. Price: per duty start, grey: the default; * some clients have their own price. Latest invoice: its month on hover; the cloud: in FreeAgent. Reminders: for the latest invoice. Suspended with a crossed-out $: for non-payment. Outstanding: all unpaid invoices.
					</span>
					{shown.pages > 1 && (
						<ButtonGroup size="sm">
							<Button color="secondary" outline disabled={shown.page === 0} onClick={() => this.setState({ page: shown.page - 1 })}>Previous</Button>
							<Button color="secondary" outline disabled>{shown.page + 1} / {shown.pages}</Button>
							<Button color="secondary" outline disabled={shown.page >= shown.pages - 1} onClick={() => this.setState({ page: shown.page + 1 })}>Next</Button>
						</ButtonGroup>
					)}
				</div>
			</React.Fragment>
		);
	}

	render() {
		const { loading, error, notice, data, editing, period } = this.state;
		return (
			<Card className="billing-overview">
				<CardBody>
					<div className="d-flex flex-wrap align-items-center justify-content-between mb-2">
						<h4 className="mb-0"><i className="mdi mdi-cash-multiple mr-2" />Billing</h4>
						{data && (
							<Input type="select" bsSize="sm" style={{ width: 'auto' }} value={period || ''} aria-label="Period"
								disabled={loading} onChange={(e) => this.load(e.target.value)}>
								{periodOptions(data.months, monthLabel).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
							</Input>
						)}
					</div>
					{this.state.freeAgentBack && (
						<Alert color={this.state.freeAgentBack.tone} toggle={() => this.setState({ freeAgentBack: null })}>{this.state.freeAgentBack.text}</Alert>
					)}
					{error && <Alert color="danger">{error}</Alert>}
					{notice && <Alert color="success" toggle={() => this.setState({ notice: '' })}>{notice}</Alert>}
					{loading && !data && <p>Loading billing...</p>}
					{data && (
						<React.Fragment>
							{this.renderIncome(data)}
							{data.freeagent && this.renderFreeAgent(data.freeagent)}
							<p className="text-muted small mb-2">
								Invoiced, received and outstanding are for the invoices of {data.period_label} (void invoices left out; deleted
								companies included). Overdue: unpaid past the due date. After reseller commission: received less the commission
								earned on those invoices (owed to resellers or paid).
								{data.income.commission && ['year', 'all'].filter((scope) => data.period !== scope).map((scope) => {
									const sums = data.income.commission[scope];
									return <span key={scope}> {sums.label}: received USD {sums.received_usd}, commission USD {sums.earned_usd} ({sums.owed_usd} owed,
										{' '}{sums.paid_usd} paid), after commission USD {sums.after_commission_usd}.</span>;
								})}
							</p>
							{this.renderTable(data)}
							<ResellerCommission commission={data.commission} onChanged={() => this.load(period, true)}
								onDialog={(open) => this.setState({ commissionDialog: open })} />
						</React.Fragment>
					)}
					{editing && (
						<EditBillingDialog companies={editing} defaultRate={data.default_rate_usd}
							onClose={() => this.setState({ editing: null })} onSaved={(message) => this.saved(message)} />
					)}
				</CardBody>
			</Card>
		);
	}
}

export default BillingOverview;
